import React, { useEffect, useState, useCallback, useRef } from "react";
import { io } from "socket.io-client";
import { convertTextLaoToThai, isLaoLanguage } from "../../../helpers/textLaosToThai";
import { SERVER_URI, SOCKET_SERVER_URI, TEXT_SERVICES } from "../../../constants";
import _ from 'lodash';
import { useSpeechSynthesis } from 'react-speech-kit';
import ReactAudioPlayer from "react-audio-player";
import styled from 'styled-components';

// Styled components for better UI
const Container = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  color: #5a54a2;
  font-size: 24px;
  margin-bottom: 20px;
  text-align: center;
`;

const InputGroup = styled.div`
  display: flex;
  margin-bottom: 20px;
`;

const Input = styled.input`
  flex: 1;
  padding: 10px 15px;
  border: 1px solid #ddd;
  border-radius: 4px 0 0 4px;
  font-size: 16px;
  outline: none;
  
  &:focus {
    border-color: #5a54a2;
  }
`;

const Button = styled.button`
  background: ${props => props.disabled ? '#cccccc' : '#5a54a2'};
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 0 4px 4px 0;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 16px;
  transition: background-color 0.3s;
  
  &:hover {
    background: ${props => props.disabled ? '#cccccc' : '#4a4492'};
  }
`;

const StatusMessage = styled.div`
  margin-top: 15px;
  padding: 10px;
  border-radius: 4px;
  text-align: center;
  background-color: ${props => props.type === 'error' ? '#ffebee' : '#e8f5e9'};
  color: ${props => props.type === 'error' ? '#c62828' : '#2e7d32'};
  display: ${props => props.visible ? 'block' : 'none'};
`;

const ShopInfo = styled.div`
  margin-bottom: 20px;
  padding: 10px;
  background-color: #e3f2fd;
  border-radius: 4px;
  text-align: center;
`;

const SettingTextToSpeech = () => {
    // Refs
    const soundPlayer = useRef(null);
    const socketRef = useRef(null);
    
    // Speech synthesis
    const { speak, voices } = useSpeechSynthesis();
    const [availableVoices, setAvailableVoices] = useState([]);
    
    // State management
    const [shop, setShop] = useState("");
    const [shopId, setShopId] = useState(localStorage.getItem("SHOP"));
    const [text, setText] = useState("");
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState({ message: "", type: "" });
    
    // Get text-to-speech settings from localStorage
    // const textToSpeech = JSON.parse(localStorage.getItem("TEXT_TO_SPEECH"));
    const textServices = JSON.parse(localStorage.getItem("TEXT_SERVICES")) || 
                        { service: TEXT_SERVICES[0], cloud: true };

    // Initialize voices for speech synthesis
    useEffect(() => {
        function setSpeechVoices() {
            setAvailableVoices(window.speechSynthesis.getVoices());
        }

        setSpeechVoices();
        if (window.speechSynthesis.onvoiceschanged !== undefined) {
            window.speechSynthesis.onvoiceschanged = setSpeechVoices;
        }
    }, []);

    // Initialize socket connection
    useEffect(() => {
        /**
         * websocket only. Polling cannot work behind the NLB with more than one ECS task —
         * its session lives in one task's memory and every request re-hashes to a random
         * task. The old connect_error handler forced clients onto exactly that transport.
         */
        socketRef.current = io(SOCKET_SERVER_URI, {
            autoConnect: false,
            transports: ['websocket'],
            upgrade: false,
            timeout: 10000,
            reconnection: true,
            reconnectionAttempts: 5,
            reconnectionDelay: 1000,
        });
        
        // Load shop data from localStorage
        const storedShop = localStorage.getItem("SHOP_DATA");
        if (storedShop) {
            try {
                const shopData = JSON.parse(storedShop);
                setShop(shopData.name);
                setShopId(shopData.id);
            } catch (error) {
                console.error("Error parsing shop data:", error);
                setStatus({
                    message: "Error loading shop data. Please refresh the page.",
                    type: "error"
                });
            }
        } else {
            setStatus({
                message: "Shop data not found. Please set up your shop first.",
                type: "error"
            });
        }

        // Handle "tts-audio" socket event
        const handleTtsAudio = (data) => {
            // console.log("TTS Audio data received:", data);
            
            if (data?.status && data?.liveId === shopId && data?.services === TEXT_SERVICES[0]) {
                try {
                    const audioUrl = `data:audio/mp3;base64,${data?.audioContent}`;
                    const audio = new Audio(audioUrl);
                    audio.play();
                    localStorage.setItem("TEXT_SERVICES", JSON.stringify({
                        cloud: true, 
                        service: TEXT_SERVICES[0]
                    }));
                    setStatus({
                        message: "Cloud text-to-speech played successfully!",
                        type: "success"
                    });
                    setLoading(false);
                    console.log("Cloud text-to-speech played successfully!");
                } catch (error) {
                    console.error("Error playing audio:", error);
                    setStatus({
                        message: "Error playing cloud audio. Falling back to local speech.",
                        type: "error"
                    });
                    fallbackToLocalSpeech();
                    setLoading(false);
                } finally {
                    closeSocket();
                }
            } else {
                if (_.isEmpty(data.services)) {
                    localStorage.setItem("TEXT_SERVICES", JSON.stringify({ cloud: false }));
                }
                closeSocket();
                fallbackToLocalSpeech();
            }
        };

        // Set up socket event listeners
        socketRef.current.on("tts-audio", handleTtsAudio);
        socketRef.current.on("connect_error", handleSocketError);

        // Cleanup on component unmount
        return () => {
            if (socketRef.current) {
                socketRef.current.off("tts-audio", handleTtsAudio);
                socketRef.current.off("connect_error", handleSocketError);
                closeSocket();
            }
        };
    }, [shopId]);

    // Handle socket connection errors
    const handleSocketError = (error) => {
        console.error("Socket connection error:", error);
        setStatus({
            message: "Connection error. Falling back to local speech.",
            type: "error"
        });
        fallbackToLocalSpeech();
    };

    // Local speech fallback
    const fallbackToLocalSpeech = useCallback(() => {
        const thaiVoice = availableVoices.find(voice => voice.lang === 'th-TH');
        
        if (!thaiVoice) {
            // If no Thai voice is available, play the fallback audio file
            if (soundPlayer.current && soundPlayer.current.audioEl.current) {
                soundPlayer.current.audioEl.current.play();
                setStatus({
                    message: "Using fallback audio file (no Thai voice found).",
                    type: "success"
                });
            }
            setLoading(false)
            return;
        }

        const languageCode = isLaoLanguage(text);
        // Convert text to Thai if necessary
        const newText = languageCode ? convertTextLaoToThai(text) : text;
        let responseText = thaiVoice ? `รหัส${newText}หมดแล้วค่ะ` : `Code ${newText} is sold out`;
        
        speak({ 
            text: responseText, 
            rate: 0.9, 
            voice: thaiVoice 
        });
        
        setStatus({
            message: "Local text-to-speech played successfully!",
            type: "success"
        });

        return () => {
            setLoading(false);
        };
    }, [text, availableVoices, speak]);

    // Cleanly disconnect socket
    const closeSocket = useCallback(() => {
        if (socketRef.current && socketRef.current.connected) {
            socketRef.current.emit("leave-live", shopId);
            socketRef.current.disconnect();
        }
    }, [shopId]);

    // Handle text input change
    const handleTextChange = (e) => {
        setText(e.target.value);
    };

    // Emit stock empty notification
    const notifyStockEmpty = () => {
        if (loading || !text.trim()) return;
        
        setLoading(true);
        setStatus({ message: "", type: "" });
        
        if (!shopId) {
            console.error("Shop ID is missing");
            setStatus({
                message: "Shop not found. Please set up your shop first.",
                type: "error"
            });
            setLoading(false);
            return;
        }

        const languageCode = isLaoLanguage(text);
        // Convert text to Thai if necessary
        const newText = languageCode ? convertTextLaoToThai(text) : text;

        try {
            /**
             * Emit only once the socket is actually connected.
             *
             * Emitting straight after connect() put the packets in socket.io's sendBuffer,
             * which is flushed on the NEXT successful connect — whenever that happens. A
             * failed attempt therefore replayed this test days later, on an unrelated
             * connection. `once('connect')` here is safe because it is a single user-driven
             * action, unlike the live screen where it stacked per sold-out code.
             */
            const emitTest = () => {
                socketRef.current.emit("join-live", shopId);
                socketRef.current.emit("stock-empty", {
                    text: `รหัส ${newText} หมดแล้วค่ะ`,
                    liveId: shopId,
                    services: textServices?.service || "GOOGLE_CLOUD",
                    shop: shopId,
                    ts: Date.now()
                });
            };

            if (socketRef.current.connected) {
                emitTest();
            } else {
                socketRef.current.once('connect', emitTest);
                socketRef.current.connect();
            }

            // Fall back to local speech if the cloud does not answer in time.
            setTimeout(() => {
                if (loading) {
                    setStatus({
                        message: "Cloud service timeout. Falling back to local speech.",
                        type: "error"
                    });
                    fallbackToLocalSpeech();
                    setLoading(false);
                }
            }, 8000);
        } catch (error) {
            console.error("Error sending stock empty notification:", error);
            setStatus({
                message: "Error connecting to service. Using local speech instead.",
                type: "error"
            });
            fallbackToLocalSpeech();
            setLoading(false);
        }
    };

    // Handle keypress (Enter key) to submit
    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && text.trim() && !loading) {
            notifyStockEmpty();
        }
    };

    return (
        <Container>
            {/* Hidden audio player for fallback */}
            <div style={{ display: 'none' }}>
                <ReactAudioPlayer
                    src="/product_empty.ogg"
                    ref={soundPlayer}
                />
            </div>

            <Title>Text-to-Speech Settings</Title>
            <p>ສຽງສຳລັບມືຖື ຫລື Ipad</p>
            
            {shop && (
                <ShopInfo>
                    <strong>Current Shop:</strong> {shop}
                </ShopInfo>
            )}
            
            <InputGroup>
                <Input
                    type="text"
                    value={text}
                    onChange={handleTextChange}
                    onKeyPress={handleKeyPress}
                    placeholder="Enter product code here..."
                    disabled={loading}
                />
                <Button 
                    onClick={notifyStockEmpty}
                    disabled={!text.trim() || loading}
                >
                    {loading ? "Processing..." : "Play Announcement"}
                </Button>
            </InputGroup>
            
            {status.message && (
                <StatusMessage 
                    type={status.type} 
                    visible={!!status.message}
                >
                    {status.message}
                </StatusMessage>
            )}
        </Container>
    );
};

export default SettingTextToSpeech;
